import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PackageIcon, FileTextIcon, CreditCardIcon, ClockIcon, CheckCircleIcon, XCircleIcon, MapPinIcon, UserIcon, PhoneIcon, MailIcon } from 'lucide-react';
interface PaymentRequestDetails {
  id: string;
  paymentRequestId: string;
  type: 'order' | 'prescription' | 'other';
  referenceId: string;
  description: string;
  status: 'pending' | 'paid' | 'expired' | 'cancelled';
  currency: string;
  customer: {
    name: string;
    phone: string;
    email: string;
  };
  shippingAddress: {
    fullName: string;
    phone: string;
    address: string;
    city: string;
    district: string;
    postalCode: string;
  };
  billingAddress?: {
    fullName: string;
    phone: string;
    address: string;
    city: string;
    district: string;
    postalCode: string;
  };
  items: Array<{
    id: string;
    name: string;
    variant?: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
    image?: string;
  }>;
  totals: {
    subtotal: number;
    discount: number;
    shipping: number;
    tax: number;
    total: number;
  };
  createdAt: string;
  paidAt?: string;
  transactionReference?: string;
}
export function PaymentRequestDetailsSection() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // Mock data - replace with API call
  const paymentRequest: PaymentRequestDetails = {
    id: '1',
    paymentRequestId: 'PR-2025-000123',
    type: 'order',
    referenceId: 'ORD-2025-001234',
    description: 'Order payment for health and wellness products',
    status: 'pending',
    currency: 'SAR',
    customer: {
      name: 'Ahmed Mohammed',
      phone: '+966 50 123 4567',
      email: 'ahmed.mohammed@example.com'
    },
    shippingAddress: {
      fullName: 'Ahmed Mohammed',
      phone: '+966 50 123 4567',
      address: 'Building 123, King Fahd Road',
      city: 'Riyadh',
      district: 'Al Olaya',
      postalCode: '12345'
    },
    items: [{
      id: '1',
      name: 'Vitamin D3 5000 IU',
      variant: '60 Capsules',
      quantity: 2,
      unitPrice: 89.0,
      lineTotal: 178.0,
      image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=200'
    }, {
      id: '2',
      name: 'Omega-3 Fish Oil',
      variant: '120 Softgels',
      quantity: 1,
      unitPrice: 125.0,
      lineTotal: 125.0,
      image: 'https://images.unsplash.com/photo-1550572017-edd951aa8f72?w=200'
    }],
    totals: {
      subtotal: 303.0,
      discount: 30.0,
      shipping: 25.0,
      tax: 45.45,
      total: 343.45
    },
    createdAt: '2025-01-15T10:30:00Z'
  };
  const getStatusBadge = (status: string) => {
    const badges = {
      pending: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: ClockIcon,
        label: 'Pending Payment'
      },
      paid: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: CheckCircleIcon,
        label: 'Paid'
      },
      expired: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        icon: XCircleIcon,
        label: 'Expired'
      },
      cancelled: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: XCircleIcon,
        label: 'Cancelled'
      }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`} data-id="element-885" data-mp="4sbtri47fyw4">
        <Icon className="w-4 h-4" data-id="element-886" data-mp="xhsk8a89l5pp" />
        {badge.label}
      </span>;
  };
  const handlePayment = async () => {
    setIsProcessing(true);
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsProcessing(false);
    setPaymentSuccess(true);
  };
  if (paymentSuccess) {
    return <div className="space-y-4" data-id="element-887" data-mp="35pfgelox945b">
        <button onClick={() => navigate('/account/payment-requests')} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4" data-id="element-888" data-mp="35p75v0g9uaxa">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-889" data-mp="2vtvoeadhaplu" />
          Back to Payment Requests
        </button>
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center" data-id="element-890" data-mp="3tfjkeu60v5wu">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-891" data-mp="3oiazwmxeipsf">
            <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-892" data-mp="3joow7k97tw8r" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-893" data-mp="3eq5b1m2g4ohg">
            Payment Successful!
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-894" data-mp="g1ybzlb1q8bl">
            Your payment has been processed successfully.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-6" data-id="element-895" data-mp="b958t1v8781m">
            <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-896" data-mp="ime9fqjwyo2l">
              <div className="text-left" data-id="element-897" data-mp="inyyj0nglet2">
                <p className="text-gray-500 mb-1" data-id="element-898" data-mp="nlqt62fhnf0y">Amount Paid</p>
                <p className="font-semibold text-gray-900" data-id="element-899" data-mp="sjseku0dzqfl">
                  {paymentRequest.totals.total.toFixed(2)}{' '}
                  {paymentRequest.currency}
                </p>
              </div>
              <div className="text-left" data-id="element-900" data-mp="11kqep17pfdsw">
                <p className="text-gray-500 mb-1" data-id="element-901" data-mp="11jxjmxi3ycql">Transaction Reference</p>
                <p className="font-semibold text-gray-900" data-id="element-902" data-mp="16irzbqks3l6h">TXN-2025-789456</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-903" data-mp="1bas5w1vctcc1">
            <button onClick={() => navigate('/account/orders')} className="px-6 py-3 bg-[#1E2B55] text-white rounded-lg font-medium hover:bg-[#162041] transition-colors" data-id="element-904" data-mp="1g8bkx3zvlt51">
              View My Orders
            </button>
            <button onClick={() => navigate('/account/payment-requests')} className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-905" data-mp="3map210atj1vm">
              Back to Payment Requests
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-4" data-id="element-906" data-mp="3qbhctxzc9eu0">
      {/* Back Button */}
      <button onClick={() => navigate('/account/payment-requests')} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4" data-id="element-907" data-mp="8vj107d38vf5">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-908" data-mp="3z27syr17qv7" />
        Back to Payment Requests
      </button>
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6" data-id="element-909" data-mp="dv5aq6tpjtj7">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-910" data-mp="dt195vja76m3">
          <div data-id="element-911" data-mp="2jpww1nnj0jt8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-912" data-mp="2fm8np2r2lk6x">
              Payment Request
            </h1>
            <p className="text-sm text-gray-500" data-id="element-913" data-mp="25ik1henmnqqr">
              Request ID: {paymentRequest.paymentRequestId}
            </p>
          </div>
          {getStatusBadge(paymentRequest.status)}
        </div>
      </div>
      {/* Status Messages */}
      {paymentRequest.status === 'expired' && <div className="bg-gray-50 border border-gray-200 rounded-xl p-4" data-id="element-914" data-mp="20rbwcutbyrje">
          <p className="text-sm text-gray-700" data-id="element-915" data-mp="20p8v1emq9bzd">
            This payment request has expired. Please contact support if you need
            assistance.
          </p>
        </div>}
      {paymentRequest.status === 'paid' && <div className="bg-green-50 border border-green-200 rounded-xl p-4" data-id="element-916" data-mp="1cu632xsmmseo">
          <p className="text-sm text-green-800" data-id="element-917" data-mp="1hspu3cm2uudw">
            This payment request has been paid. Transaction reference:{' '}
            {paymentRequest.transactionReference}
          </p>
        </div>}
      {paymentRequest.status === 'cancelled' && <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-id="element-918" data-mp="3c68nca92rqte">
          <p className="text-sm text-red-800" data-id="element-919" data-mp="okqmbmd0amar">
            This payment request has been cancelled.
          </p>
        </div>}
      {/* Payment Summary Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6" data-id="element-920" data-mp="evqlryiuviug">
        <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-921" data-mp="etm7qnr8t3gg">
          Payment Summary
        </h2>
        <div className="space-y-3" data-id="element-922" data-mp="askgat3khvp1">
          <div className="flex justify-between text-sm" data-id="element-923" data-mp="5vkamjamquyg">
            <span className="text-gray-600" data-id="element-924" data-mp="5sxvnyletd67">Type:</span>
            <span className="font-medium text-gray-900 capitalize" data-id="element-925" data-mp="vfvawlgk69t">
              {paymentRequest.type}
            </span>
          </div>
          <div className="flex justify-between text-sm" data-id="element-926" data-mp="3s4bup7rk54u5">
            <span className="text-gray-600" data-id="element-927" data-mp="3o28gwchf1o5p">Reference:</span>
            <span className="font-medium text-gray-900" data-id="element-928" data-mp="1qavrlbea9xia">
              {paymentRequest.referenceId}
            </span>
          </div>
          <div className="flex justify-between text-sm" data-id="element-929" data-mp="1maln63653f5j">
            <span className="text-gray-600" data-id="element-930" data-mp="3476th66abs6u">Description:</span>
            <span className="font-medium text-gray-900 text-right" data-id="element-931" data-mp="346x2pgdf1h03">
              {paymentRequest.description}
            </span>
          </div>
        </div>
      </div>
      {/* Order Items */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6" data-id="element-932" data-mp="3dxi9vs75ntum">
        <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-933" data-mp="3hxiopdy62727">
          Order Items
        </h2>
        <div className="space-y-4" data-id="element-934" data-mp="2l9cm0wkt1oqk">
          {paymentRequest.items.map(item => <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0" data-id="element-935" data-mp="2q6md0gnue8gi">
              {item.image && <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover" data-id="element-936" data-mp="2z7u8ys2vyx7f" />}
              <div className="flex-1" data-id="element-937" data-mp="9l4r6d0mqoru">
                <h3 className="text-sm font-medium text-gray-900" data-id="element-938" data-mp="egtriikh49p7">
                  {item.name}
                </h3>
                {item.variant && <p className="text-xs text-gray-500 mt-1" data-id="element-939" data-mp="1j2wos66vmm63">{item.variant}</p>}
                <p className="text-xs text-gray-500 mt-1" data-id="element-940" data-mp="1o1hf9dwbtrhc">
                  Qty: {item.quantity}
                </p>
              </div>
              <div className="text-right" data-id="element-941" data-mp="1xtwy0dfg6k3a">
                <p className="text-sm font-medium text-gray-900" data-id="element-942" data-mp="1xsu745i4khnd">
                  {item.lineTotal.toFixed(2)} {paymentRequest.currency}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-943" data-mp="1546j425zre64">
                  {item.unitPrice.toFixed(2)} each
                </p>
              </div>
            </div>)}
        </div>
        {/* Totals */}
        <div className="mt-6 pt-4 border-t border-gray-200 space-y-2" data-id="element-944" data-mp="1e6plkl5v7oru">
          <div className="flex justify-between text-sm" data-id="element-945" data-mp="2ljr8wpg7hykw">
            <span className="text-gray-600" data-id="element-946" data-mp="2llar4llfajpb">Subtotal:</span>
            <span className="text-gray-900" data-id="element-947" data-mp="2pjs07aoem49d">
              {paymentRequest.totals.subtotal.toFixed(2)}{' '}
              {paymentRequest.currency}
            </span>
          </div>
          {paymentRequest.totals.discount > 0 && <div className="flex justify-between text-sm" data-id="element-948" data-mp="9wo0e6hgkk6t">
              <span className="text-gray-600" data-id="element-949" data-mp="54cv0o3y866v">Discount:</span>
              <span className="text-green-600" data-id="element-950" data-mp="62wc3j0mngi">
                -{paymentRequest.totals.discount.toFixed(2)}{' '}
                {paymentRequest.currency}
              </span>
            </div>}
          <div className="flex justify-between text-sm" data-id="element-951" data-mp="3md9l305t1ada">
            <span className="text-gray-600" data-id="element-952" data-mp="3mbq3n68ewt9x">Shipping:</span>
            <span className="text-gray-900" data-id="element-953" data-mp="3ib67o5zkrvpd">
              {paymentRequest.totals.shipping.toFixed(2)}{' '}
              {paymentRequest.currency}
            </span>
          </div>
          <div className="flex justify-between text-sm" data-id="element-954" data-mp="1gk1f97ptir3z">
            <span className="text-gray-600" data-id="element-955" data-mp="1gjjm4jygct0a">Tax (15%):</span>
            <span className="text-gray-900" data-id="element-956" data-mp="1bkyjyezn3z97">
              {paymentRequest.totals.tax.toFixed(2)} {paymentRequest.currency}
            </span>
          </div>
          <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-200" data-id="element-957" data-mp="1uo0doq7nc77g">
            <span className="text-gray-900" data-id="element-958" data-mp="1zieg3ckq7elm">Total:</span>
            <span className="text-[#1E2B55]" data-id="element-959" data-mp="1lqg9p9iefwt5">
              {paymentRequest.totals.total.toFixed(2)} {paymentRequest.currency}
            </span>
          </div>
        </div>
      </div>
      {/* Customer & Shipping Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-960" data-mp="2ebpxob9cd364">
        {/* Customer Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6" data-id="element-961" data-mp="2iemk6d205jfc">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-962" data-mp="24ev486rxdsyp">
            Customer Information
          </h2>
          <div className="space-y-3" data-id="element-963" data-mp="29cwlcm5sji7j">
            <div className="flex items-start gap-3" data-id="element-964" data-mp="t0r6qqm4pmow">
              <UserIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-965" data-mp="sz6dr5fgqbt5" />
              <div data-id="element-966" data-mp="xzuc6c60tuxe">
                <p className="text-sm font-medium text-gray-900" data-id="element-967" data-mp="y15iojqupgs7">
                  {paymentRequest.customer.name}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-968" data-mp="lhrr8w1sgfat">
              <PhoneIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-969" data-mp="lj2tgrfl5ezt" />
              <div data-id="element-970" data-mp="zfge29ff1wii">
                <p className="text-sm text-gray-600" data-id="element-971" data-mp="zfydtg1aesv6">
                  {paymentRequest.customer.phone}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-972" data-mp="6ranh61oldar">
              <MailIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-973" data-mp="6pptpin6xydy" />
              <div data-id="element-974" data-mp="2s1wg8y0ztiw">
                <p className="text-sm text-gray-600" data-id="element-975" data-mp="2oe3pc0enb58">
                  {paymentRequest.customer.email}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Shipping Address */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6" data-id="element-976" data-mp="3k6yx7v39fqg1">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-977" data-mp="3k4w0ytbil12v">
            Shipping Address
          </h2>
          <div className="flex items-start gap-3" data-id="element-978" data-mp="3sumfptz06vtn">
            <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-979" data-mp="3svxh5n4z7rhl" />
            <div className="text-sm text-gray-600" data-id="element-980" data-mp="1nicla5zbtqi">
              <p className="font-medium text-gray-900 mb-1" data-id="element-981" data-mp="1lyt7ap112ki">
                {paymentRequest.shippingAddress.fullName}
              </p>
              <p data-id="element-982" data-mp="bemlpt8z4kdf">{paymentRequest.shippingAddress.address}</p>
              <p data-id="element-983" data-mp="bfxs80tt0688">
                {paymentRequest.shippingAddress.district},{' '}
                {paymentRequest.shippingAddress.city}
              </p>
              <p data-id="element-984" data-mp="kdrnr9jquvuc">{paymentRequest.shippingAddress.postalCode}</p>
              <p className="mt-2" data-id="element-985" data-mp="pe5k3ubzghcj">{paymentRequest.shippingAddress.phone}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Payment Section - Only show if pending */}
      {paymentRequest.status === 'pending' && <>
          {/* Confirmation */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6" data-id="element-986" data-mp="2r7lnlrt65qox">
            <label className="flex items-start gap-3 cursor-pointer" data-id="element-987" data-mp="2w4lshzea8neg">
              <input type="checkbox" checked={isConfirmed} onChange={e => setIsConfirmed(e.target.checked)} className="mt-1 w-4 h-4 text-[#1E2B55] border-gray-300 rounded focus:ring-[#1E2B55]" data-id="element-988" data-mp="1da95zf97ilcb" />
              <span className="text-sm text-gray-700" data-id="element-989" data-mp="zicixpmeb1ms">
                I confirm that the above order and shipping details are correct.
              </span>
            </label>
          </div>
          {/* Payment Methods Placeholder */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6" data-id="element-990" data-mp="1s6zxz3gjwurw">
            <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-991" data-mp="1n6cagrrj6w2f">
              Payment Method
            </h2>
            <p className="text-sm text-gray-500 mb-4" data-id="element-992" data-mp="1i7slsjljz66d">
              Select your preferred payment method:
            </p>
            {/* Mock Payment Methods - Replace with actual CheckoutPaymentMethods component */}
            <div className="space-y-3" data-id="element-993" data-mp="6v8afao3g569">
              <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-[#1E2B55] transition-colors" data-id="element-994" data-mp="89svsnxpes29">
                <input type="radio" name="payment" className="w-4 h-4 text-[#1E2B55]" defaultChecked data-id="element-995" data-mp="d8m7qbxceb0b" />
                <CreditCardIcon className="w-5 h-5 text-gray-400" data-id="element-996" data-mp="4760ue5qkr0u" />
                <span className="text-sm font-medium text-gray-900" data-id="element-997" data-mp="3loz0bqun7gnh">
                  Credit / Debit Card
                </span>
              </label>
              <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-[#1E2B55] transition-colors" data-id="element-998" data-mp="3qhs1y5ute8vc">
                <input type="radio" name="payment" className="w-4 h-4 text-[#1E2B55]" data-id="element-999" data-mp="3gr5r0oy1m6iz" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" className="h-5" data-id="element-1000" data-mp="37pg0rmfwddms" />
                <span className="text-sm font-medium text-gray-900" data-id="element-1001" data-mp="2o5h57m3r3qjl">
                  PayPal
                </span>
              </label>
            </div>
            <button onClick={handlePayment} disabled={!isConfirmed || isProcessing} className="w-full mt-6 px-6 py-4 bg-[#1E2B55] text-white rounded-lg font-semibold hover:bg-[#162041] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-1002" data-mp="2y2lj6yng0fpb">
              {isProcessing ? 'Processing...' : `Pay ${paymentRequest.totals.total.toFixed(2)} ${paymentRequest.currency}`}
            </button>
          </div>
        </>}
    </div>;
}